import React, { useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ImagePlus, X } from 'lucide-react';

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const StoryCreator = ({ token, API, t, isRTL, onClose, onCreated }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError(t('mediaRequired'));
      return;
    }
    setError('');
    const dataUrl = await fileToDataUrl(f);
    setPreview(dataUrl);
  };

  const publish = async () => {
    if (!preview || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${API}/stories`,
        { media_url: preview, media_type: 'image' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated && onCreated();
      onClose && onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="story-creator"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      <header className="flex items-center justify-between px-4 py-3 text-white">
        <button
          onClick={onClose}
          data-testid="story-creator-close"
          className="p-1.5 rounded-full hover:bg-white/10"
          aria-label={t('close')}
        >
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </button>
        <h2 className="text-base font-semibold">{t('newStory')}</h2>
        <div className="w-8" />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-6">
        {preview ? (
          <div className="relative max-h-full max-w-md w-full">
            <img
              src={preview}
              alt="story preview"
              data-testid="story-creator-preview"
              className="w-full max-h-[70vh] object-contain rounded-2xl"
            />
            <button
              onClick={() => setPreview(null)}
              data-testid="story-creator-clear"
              className="absolute top-2 end-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-black/80"
              aria-label={t('close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="story-creator-pick"
            className="w-full max-w-sm aspect-[9/16] rounded-2xl border-2 border-dashed border-white/30 text-white/70 flex flex-col items-center justify-center gap-3 hover:border-white/60 hover:text-white"
          >
            <ImagePlus className="w-10 h-10" />
            <span className="text-sm font-medium">{t('storyPickMedia')}</span>
          </button>
        )}
      </div>

      {error && (
        <p data-testid="story-creator-error" className="text-center text-red-300 text-sm pb-2">
          {error}
        </p>
      )}

      <div className="px-4 pb-6 flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          data-testid="story-creator-input"
        />
        {preview ? (
          <button
            onClick={publish}
            disabled={busy}
            data-testid="story-creator-publish"
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold disabled:opacity-50"
          >
            {busy ? t('storyPublishing') : t('storyPublish')}
          </button>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="story-creator-pick-cta"
            className="flex-1 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-gray-100"
          >
            {t('storyPickMedia')}
          </button>
        )}
      </div>
    </div>
  );
};

export default StoryCreator;
